import React, { useContext, useEffect, useState } from "react";
import "../css/SignCss.css";
import "../css/landingLogin.css";
import googleIcon from "../images/google-icon.png";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "react-tippy/dist/tippy.css";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { showCustomToast } from "./Misc/CustomToast";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { LanguageContext } from "../context/LanguageContext";
import ThailandFlag from "../images/flags/thai-flag.png";
import USFlag from "../images/flags/uk-flag-icon.png";
import FranceFlag from "../images/flags/french-flag-icon.png";
import LoadingOverlay from "./Misc/LoadingOverlay";
import PhoneNumberInputNoTitleV2 from "./PhoneNumberInputNoTitleV2";

export default function Login() {
  const navigate = useNavigate();
  const { translate, language, setLanguage } = useContext(LanguageContext);
  const { signInWithGoogle, currentUser } = useAuth();
  const functions = getFunctions(undefined, "us-central1");
  const [loadingOverlay, setLoadingOverlay] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactMode, setContactMode] = useState("demo");
  const [contactForm, setContactForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  const features = [
    "Free full setup",
    "Unlimited staff & therapists",
    "Up to 100 active clients per month",
    "Fully customized to your workflow",
    "One flat monthly fee (~EUR 199)",
    "Additional pricing available for larger facilities",
  ];

  const coreItems = [
    {
      title: "Client management",
      text: "Track active clients, treatment progress, and essential operational information in one place.",
    },
    {
      title: "Staff coordination",
      text: "Give unlimited access to therapists and team members without adding complexity to your pricing.",
    },
    {
      title: "Notes and reports",
      text: "Keep documentation organized and make follow-up easier for the whole team.",
    },
    {
      title: "Custom workflows",
      text: "Adapt the platform to your admissions, care process, and internal way of working.",
    },
  ];

  const steps = [
    {
      number: "01",
      title: "Quick discovery call",
      text: "We understand your center, your workflow, and the features you need.",
    },
    {
      number: "02",
      title: "Free setup and customization",
      text: "We configure Rehapp around your way of working, not the other way around.",
    },
    {
      number: "03",
      title: "Start using it fast",
      text: "Your team can begin using the system quickly, with room for future improvements.",
    },
  ];

  const handleLanguageChange = (event) => {
    setLanguage(event.target.value);
  };

  const openContactModal = (mode) => {
    setContactMode(mode);
    setContactModalOpen(true);
  };

  const closeContactModal = () => {
    setContactModalOpen(false);
  };

  const handleContactFieldChange = (event) => {
    const { name, value } = event.target;

    setContactForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhoneChange = (value) => {
    setContactForm((prev) => ({
      ...prev,
      phone: value || "",
    }));
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();

    const requiredFields = [
      contactForm.firstName,
      contactForm.lastName,
      contactForm.email,
      contactForm.phone,
    ];

    if (requiredFields.some((field) => !field.trim())) {
      showCustomToast("Please complete all fields before sending.", "error");
      return;
    }

    setLoadingOverlay(true);

    try {
      const sendLandingContactRequest = httpsCallable(
        functions,
        "sendLandingContactRequest"
      );

      const result = await sendLandingContactRequest({
        mode: contactMode,
        firstName: contactForm.firstName,
        lastName: contactForm.lastName,
        email: contactForm.email,
        phone: contactForm.phone,
      });

      if (!result.data?.success) {
        throw new Error(result.data?.error?.message || "Email sending failed");
      }

      showCustomToast(
        contactMode === "demo"
          ? "Your demo request has been sent."
          : "Your contact request has been sent.",
        "success"
      );

      setContactModalOpen(false);
      setContactForm({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
      });
    } catch (error) {
      showCustomToast(
        "Unable to send your request right now. Please try again.",
        "error"
      );
    } finally {
      setLoadingOverlay(false);
    }
  };

  useEffect(() => {
    async function fetchUserRoleAndNavigate() {
      if (!currentUser) {
        return;
      }

      setLoadingOverlay(true);

      try {
        const docRef = doc(db, "roles", currentUser.uid);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
          console.log(translate("NoRoleFoundForUser"));
        }

        navigate("/");
      } finally {
        setLoadingOverlay(false);
      }
    }

    fetchUserRoleAndNavigate();
  }, [currentUser, navigate, translate]);

  function handleSignInWithGoogle() {
    setLoadingOverlay(true);
    signInWithGoogle()
      .then(() => {
        showCustomToast(
          `${translate("SuccessfullyLoggedInWithGoogle")}`,
          "success"
        );
      })
      .catch((error) => {
        if (error.code === "permission-denied") {
          showCustomToast(
            `${translate("MustLogInWithGmailOrGsuite")}`,
            "error"
          );
        } else {
          showCustomToast(
            `${translate("Failed to log in with your Google account")}`,
            "error"
          );
        }
        setLoadingOverlay(false);
      });
  }

  return (
    <div className="rehapp-landing">
      {loadingOverlay && <LoadingOverlay />}

      <div className="rehapp-landing__background">
        <div className="rehapp-landing__glow rehapp-landing__glow--top" />
        <div className="rehapp-landing__glow rehapp-landing__glow--right" />
        <div className="rehapp-landing__glow rehapp-landing__glow--bottom" />
      </div>

      <header className="rehapp-landing__header">
        <div className="rehapp-landing__shell rehapp-landing__header-row">
          <div className="rehapp-landing__brand">
            <div className="rehapp-landing__brand-mark">R</div>
            <div>
              <p className="rehapp-landing__brand-name">Rehapp</p>
              <p className="rehapp-landing__brand-subtitle">
                Custom EHR for rehab centers
              </p>
            </div>
          </div>

          <nav className="rehapp-landing__nav">
            <a href="#features">Features</a>
            <a href="#pricing">Pricing</a>
            <a href="#process">Process</a>
            <a href="#contact">Contact</a>
          </nav>

          <div className="rehapp-landing__header-actions">
            <button
              type="button"
              className="rehapp-landing__button rehapp-landing__button--ghost"
              onClick={() => openContactModal("demo")}
            >
              Book a demo
            </button>
            <a className="rehapp-landing__button rehapp-landing__button--primary" href="#test-app">
              Start for free
            </a>
          </div>
        </div>
      </header>

      <main className="rehapp-landing__main">
        <section className="rehapp-landing__hero rehapp-landing__shell">
          <div className="rehapp-landing__hero-copy">
            <div className="rehapp-landing__eyebrow">
              <span className="rehapp-landing__eyebrow-dot" />
              Built for modern rehab centers
            </div>

            <h1 className="rehapp-landing__hero-title">
              The EHR that adapts to your rehab center.
            </h1>

            <p className="rehapp-landing__hero-text">
              Free setup. Full customization. One simple monthly fee. Rehapp
              is designed to make daily operations easier without the heavy
              cost and rigidity of traditional rehab software.
            </p>

            <div className="rehapp-landing__hero-actions">
              <button
                type="button"
                className="rehapp-landing__button rehapp-landing__button--primary"
                onClick={() => openContactModal("demo")}
              >
                Book a demo
              </button>
              <a
                className="rehapp-landing__button rehapp-landing__button--ghost"
                href="#test-app"
              >
                Get started for free
              </a>
            </div>

            <div className="rehapp-landing__feature-grid">
              {features.map((item) => (
                <div key={item} className="rehapp-landing__feature-card">
                  <div className="rehapp-landing__feature-dot" />
                  <p>{item}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rehapp-landing__preview-card">
            <div className="rehapp-landing__preview-surface">
              <div className="rehapp-landing__preview-top">
                <div>
                  <p className="rehapp-landing__preview-label">
                    Dashboard preview
                  </p>
                  <p className="rehapp-landing__preview-title">
                    Rehapp Overview
                  </p>
                </div>
                <div className="rehapp-landing__preview-badge">Active system</div>
              </div>

              <div className="rehapp-landing__stats-grid">
                <div className="rehapp-landing__stat-card">
                  <p className="rehapp-landing__stat-label">Staff</p>
                  <p className="rehapp-landing__stat-value">Unlimited</p>
                  <p className="rehapp-landing__stat-note">Unlimited access</p>
                </div>
                <div className="rehapp-landing__stat-card">
                  <p className="rehapp-landing__stat-label">Active Clients</p>
                  <p className="rehapp-landing__stat-value">100</p>
                  <p className="rehapp-landing__stat-note">Included per month</p>
                </div>
                <div className="rehapp-landing__stat-card">
                  <p className="rehapp-landing__stat-label">Pricing</p>
                  <p className="rehapp-landing__stat-value">EUR 199</p>
                  <p className="rehapp-landing__stat-note">Flat monthly fee</p>
                </div>
              </div>

              <div className="rehapp-landing__insight-card">
                <div className="rehapp-landing__insight-header">
                  <p>Why centers choose Rehapp</p>
                  <span>Simple setup</span>
                </div>
                {[85, 92, 78].map((value, index) => (
                  <div key={value} className="rehapp-landing__insight-row">
                    <div className="rehapp-landing__insight-labels">
                      <span>
                        {index === 0
                          ? "Workflow fit"
                          : index === 1
                          ? "Ease of use"
                          : "Speed of onboarding"}
                      </span>
                      <span>{value}%</span>
                    </div>
                    <div className="rehapp-landing__progress-track">
                      <div
                        className="rehapp-landing__progress-fill"
                        style={{ width: `${value}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="features" className="rehapp-landing__section rehapp-landing__shell">
          <div className="rehapp-landing__section-heading">
            <p className="rehapp-landing__section-kicker">Why Rehapp</p>
            <h2>
              Most rehab software is too rigid. Rehapp is built to stay
              flexible.
            </h2>
            <p>
              Traditional systems often come with complex setup, user-based
              pricing, and limited room for adaptation. Rehapp keeps things
              simple while giving your center the flexibility it actually needs.
            </p>
          </div>

          <div className="rehapp-landing__core-grid">
            {coreItems.map((item) => (
              <div key={item.title} className="rehapp-landing__core-card">
                <div className="rehapp-landing__core-icon">*</div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="pricing" className="rehapp-landing__section rehapp-landing__shell">
          <div className="rehapp-landing__pricing-layout">
            <div className="rehapp-landing__pricing-intro">
              <p className="rehapp-landing__section-kicker rehapp-landing__section-kicker--pink">
                Pricing
              </p>
              <h2>Simple and transparent.</h2>
              <p>
                No complicated quote process. No per-user pricing. No expensive
                setup.
              </p>
            </div>

            <div className="rehapp-landing__pricing-card">
              <div className="rehapp-landing__pricing-top">
                <div>
                  <p className="rehapp-landing__pricing-plan">Standard plan</p>
                  <div className="rehapp-landing__pricing-value">
                    <span>EUR 199</span>
                    <span>/ month</span>
                  </div>
                  <p className="rehapp-landing__pricing-text">
                    Includes everything needed for a modern rehab center, with
                    free setup and custom adaptation to your workflow.
                  </p>
                </div>
                <a
                  className="rehapp-landing__button rehapp-landing__button--primary"
                  href="#test-app"
                >
                  Start with Rehapp
                </a>
              </div>

              <div className="rehapp-landing__pricing-grid">
                {[
                  "Unlimited staff and therapists",
                  "Up to 100 active clients per month",
                  "Free setup and onboarding",
                  "Customization included",
                  "Core features included",
                  "Additional pricing for larger facilities",
                ].map((item) => (
                  <div key={item} className="rehapp-landing__pricing-item">
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="process" className="rehapp-landing__section rehapp-landing__shell">
          <div className="rehapp-landing__section-heading rehapp-landing__section-heading--narrow">
            <p className="rehapp-landing__section-kicker rehapp-landing__section-kicker--green">
              How it works
            </p>
            <h2>Get started in days, not months.</h2>
          </div>

          <div className="rehapp-landing__steps-grid">
            {steps.map((step) => (
              <div key={step.number} className="rehapp-landing__step-card">
                <p className="rehapp-landing__step-number">{step.number}</p>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="rehapp-landing__section rehapp-landing__shell">
          <div className="rehapp-landing__audience-card">
            <div>
              <p className="rehapp-landing__section-kicker">
                Built for rehab centers
              </p>
              <h2>A modern EHR designed around your operations.</h2>
              <p>
                Rehapp is ideal for addiction treatment centers, mental health
                facilities, international clinics, and boutique rehab centers
                looking for a simpler and more adaptable system.
              </p>
            </div>

            <div className="rehapp-landing__audience-list">
              {[
                "Addiction treatment centers",
                "Mental health facilities",
                "International rehab clinics",
                "Boutique or growing centers",
              ].map((item) => (
                <div key={item} className="rehapp-landing__audience-item">
                  {item}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          id="test-app"
          className="rehapp-landing__section rehapp-landing__shell rehapp-landing__section--login"
        >
          <div className="rehapp-landing__login-card">
            <div className="rehapp-landing__login-copy">
              <p className="rehapp-landing__section-kicker">Test the app</p>
              <h2>See how Rehapp works with your team in mind.</h2>
              <p>
                Sign in with Google to access the demo environment and explore
                the product. This is the quickest way to test the workflow.
              </p>
            </div>

            <div className="rehapp-landing__login-panel">
              <div className="rehapp-landing__login-panel-header">
                <div className="rehapp-landing__brand rehapp-landing__brand--compact">
                  <div className="rehapp-landing__brand-mark">R</div>
                  <div>
                    <p className="rehapp-landing__brand-name">Rehapp</p>
                    <p className="rehapp-landing__brand-subtitle">
                      Demo access
                    </p>
                  </div>
                </div>
              </div>

              <div className="rehapp-landing__login-title">
                {translate("WelcomeToRehappApp")}
              </div>
              <div className="rehapp-landing__login-subtitle">
                {translate("ContinueWithGoogle")}
              </div>

              <div className="rehapp-landing__login-actions">
                <Button
                  variant="contained"
                  onClick={handleSignInWithGoogle}
                  sx={{
                    borderRadius: "18px",
                    paddingTop: "12px",
                    paddingBottom: "12px",
                    backgroundColor: "#f8fafc",
                    color: "#020617",
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#e2e8f0",
                    },
                  }}
                  fullWidth
                >
                  <img
                    alt="Google icon"
                    src={googleIcon}
                    className="google-icon"
                  />
                  {translate("LogInWithGoogle")}
                </Button>
              </div>

              <div className="rehapp-landing__language">
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="language-select-label">Language</InputLabel>
                  <Select
                    labelId="language-select-label"
                    id="language-select"
                    value={language}
                    onChange={handleLanguageChange}
                    label="Language"
                    renderValue={(selected) => (
                      <Box display="flex" alignItems="center">
                        {selected === "en" && (
                          <img
                            src={USFlag}
                            alt="US Flag"
                            style={{ width: 20, marginRight: 10 }}
                          />
                        )}
                        {selected === "fr" && (
                          <img
                            src={FranceFlag}
                            alt="France Flag"
                            style={{ width: 20, marginRight: 10 }}
                          />
                        )}
                        {selected === "th" && (
                          <img
                            src={ThailandFlag}
                            alt="Thailand Flag"
                            style={{ width: 20, marginRight: 10 }}
                          />
                        )}
                        {selected === "en"
                          ? "English"
                          : selected === "fr"
                          ? "Francais"
                          : "Thai"}
                      </Box>
                    )}
                  >
                    <MenuItem value="en">
                      <Box display="flex" alignItems="center">
                        <img
                          src={USFlag}
                          alt="US Flag"
                          style={{ width: 20, marginRight: 10 }}
                        />
                        English
                      </Box>
                    </MenuItem>
                    <MenuItem value="fr">
                      <Box display="flex" alignItems="center">
                        <img
                          src={FranceFlag}
                          alt="France Flag"
                          style={{ width: 20, marginRight: 10 }}
                        />
                        Francais
                      </Box>
                    </MenuItem>
                    <MenuItem value="th">
                      <Box display="flex" alignItems="center">
                        <img
                          src={ThailandFlag}
                          alt="Thailand Flag"
                          style={{ width: 20, marginRight: 10 }}
                        />
                        Thai
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </div>
            </div>
          </div>
        </section>

        <section
          id="contact"
          className="rehapp-landing__section rehapp-landing__shell rehapp-landing__section--contact"
        >
          <div className="rehapp-landing__contact-card">
            <div className="rehapp-landing__section-heading rehapp-landing__section-heading--narrow">
              <p className="rehapp-landing__section-kicker">Ready to start</p>
              <h2>
                Replace complexity with a system that fits your center.
              </h2>
              <p>
                Start with free setup, full customization, and a simple flat
                monthly fee.
              </p>
            </div>

            <div className="rehapp-landing__contact-actions">
              <button
                type="button"
                className="rehapp-landing__button rehapp-landing__button--primary"
                onClick={() => openContactModal("demo")}
              >
                Book a demo
              </button>
              <button
                type="button"
                className="rehapp-landing__button rehapp-landing__button--ghost"
                onClick={() => openContactModal("contact")}
              >
                Contact sales
              </button>
            </div>
          </div>
        </section>
      </main>

      <Dialog
        open={contactModalOpen}
        onClose={closeContactModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "24px",
            padding: "8px",
          },
        }}
      >
        <form onSubmit={handleContactSubmit}>
          <DialogTitle sx={{ fontWeight: 700, paddingBottom: "8px" }}>
            {contactMode === "demo" ? "Book a demo" : "Contact sales"}
          </DialogTitle>
          <DialogContent>
            <p className="rehapp-landing__modal-copy">
              {contactMode === "demo"
                ? "Leave your details and we will get back to you to organize a demo of Rehapp."
                : "Leave your details and our team will contact you to discuss your center and your needs."}
            </p>

            <div className="rehapp-landing__modal-grid">
              <TextField
                label="First name"
                name="firstName"
                value={contactForm.firstName}
                onChange={handleContactFieldChange}
                fullWidth
                required
              />
              <TextField
                label="Last name"
                name="lastName"
                value={contactForm.lastName}
                onChange={handleContactFieldChange}
                fullWidth
                required
              />
            </div>

            <div className="rehapp-landing__modal-field">
              <TextField
                label="Email"
                name="email"
                type="email"
                value={contactForm.email}
                onChange={handleContactFieldChange}
                fullWidth
                required
              />
            </div>

            <div className="rehapp-landing__modal-field">
              <label className="rehapp-landing__modal-label" htmlFor="landing-phone">
                Phone number
              </label>
              <div id="landing-phone" className="rehapp-landing__phone-input">
                <PhoneNumberInputNoTitleV2
                  value={contactForm.phone}
                  onChange={handlePhoneChange}
                  placeholder="Enter phone number"
                />
              </div>
            </div>
          </DialogContent>
          <DialogActions sx={{ padding: "0 24px 24px" }}>
            <Button
              onClick={closeContactModal}
              sx={{
                borderRadius: "14px",
                textTransform: "none",
                color: "#526277",
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                borderRadius: "14px",
                paddingLeft: "18px",
                paddingRight: "18px",
                textTransform: "none",
                backgroundColor: "#1565c0",
                "&:hover": {
                  backgroundColor: "#0d47a1",
                },
              }}
            >
              Send request
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ToastContainer
        position="top-center"
        autoClose={1000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </div>
  );
}

